import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Article } from "@/types/content";
import { siteInfo } from "@/mocks/categories";
import { formatHeat } from "@/utils/format";

interface HeroSectionProps {
  highlight: Article;
}

const stats = [
  { value: "42", label: "今日热议话题" },
  { value: "8", label: "实时楼同步中" },
  { value: "25 分钟", label: "平均更新间隔" },
];

export default function HeroSection({ highlight }: HeroSectionProps) {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/rank?q=${encodeURIComponent(q)}` : "/rank");
  };

  return (
    <section className="w-full border-b border-background-200 bg-background-100">
      <div className="grid grid-cols-1 gap-8 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-12 lg:gap-12 lg:py-16">
        <div className="flex flex-col justify-center lg:col-span-7">
          <span className="mb-4 inline-flex w-fit items-center gap-2 rounded-full bg-background-50 px-3 py-1.5 font-label text-xs font-medium text-primary-700">
            <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-primary-500" />
            实时热点 · 持续跟踪中
          </span>

          <h1 className="font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-5xl">
            <span className="text-primary-600">{siteInfo.name}</span>
            <span className="mt-2 block text-2xl font-bold text-foreground-800 md:text-4xl">
              一眼看懂今天的热点
            </span>
          </h1>

          <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            我们把碎片化的现场直播、秒删截图与实时楼内容，重新梳理成清晰的时间线。不看吵闹的站队，只把一件事的来龙去脉讲完——这就是 {siteInfo.name} 想做的事。
          </p>

          <form onSubmit={handleSearch} role="search" className="mt-7 flex w-full max-w-xl flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3">
              <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索话题，例如「音乐节」「热梗」"
                aria-label="搜索话题关键词"
                className="w-full bg-transparent font-label text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜一搜
            </button>
          </form>

          <dl className="mt-8 flex flex-wrap gap-x-8 gap-y-4">
            {stats.map((item) => (
              <div key={item.label} className="flex flex-col">
                <dt className="font-label text-xs text-foreground-500">{item.label}</dt>
                <dd className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="lg:col-span-5">
          <Link
            to={`/article/${highlight.id}`}
            className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-background-300 bg-background-50 transition-colors hover:border-primary-400"
          >
            <div className="relative h-56 w-full overflow-hidden bg-background-200 md:h-64">
              <img
                src={highlight.cover}
                alt={`${highlight.title} 话题配图`}
                title={`${highlight.title} 相关热点`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-2.5 py-1 font-label text-xs font-medium text-background-50">
                <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-background-50" />
                正在实时更新
              </span>
            </div>
            <div className="flex flex-1 flex-col p-4 md:p-5">
              <div className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
                <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-secondary-800">{highlight.categoryName}</span>
                <span>{highlight.publishedAt}</span>
              </div>
              <h2 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-xl">
                {highlight.title}
              </h2>
              <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{highlight.summary}</p>
              <div className="mt-4 flex items-center justify-between font-label text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1 text-primary-700">
                  <i className="ri-fire-line" aria-hidden="true"></i>
                  {formatHeat(highlight.heat)} 热度
                </span>
                <span className="inline-flex items-center gap-1 font-medium text-primary-700">
                  进入实时楼
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}